import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import {
    barGraphCharacterWidth,
    barGraphLabelAlignRightMinWidth,
    barGraphLabelAlignTopMinHeight,
    barGraphLabelHorizontalOffset,
    barGraphLabelVerticalOffset
} from '../Config';
import { spring } from './transition';
import { BarSegment } from './types';

export const BarChartBar = ({
    bar,
    showLabels,
    disableAnimation,
    overflowLabels,
    allowLink,
    hoveredBarId,
    setHoveredBar
}: {
    bar: BarSegment;
    showLabels?: boolean;
    overflowLabels: 'horizontal' | 'vertical' | undefined;
    disableAnimation: boolean;
    allowLink: boolean;
    hoveredBarId: string | undefined;
    setHoveredBar: (bar: BarSegment | undefined) => void;
}) => {
    const { width, height, color, label, data } = bar;

    const labelLength = bar.label ? bar.label.length : 0;

    // Determine if the label is too big to fit in the bar
    const hideLabel =
        labelLength === 0 ||
        bar.width < labelLength * barGraphCharacterWidth ||
        (bar.height < barGraphLabelAlignTopMinHeight && overflowLabels !== 'vertical');

    // if height/width is too small, align the label above/to the right of the bar depending on layout.
    const shouldLabelAlignTop = height < barGraphLabelAlignTopMinHeight && overflowLabels === 'vertical';
    const shouldLabelAlignRight = width < barGraphLabelAlignRightMinWidth && overflowLabels === 'horizontal';

    const labelY = shouldLabelAlignTop ? -barGraphLabelVerticalOffset : height / 2;
    const labelX = shouldLabelAlignRight ? width + barGraphLabelHorizontalOffset : width / 2;
    const labelFill = shouldLabelAlignTop || shouldLabelAlignRight ? 'currentColor' : 'white';

    const opacity = hoveredBarId ? (hoveredBarId === bar.key ? 1 : 0.25) : 1;

    const barComponent = (
        <>
            {disableAnimation ? (
                <rect
                    height={height}
                    width={width}
                    fill={color}
                    opacity={opacity}
                    onMouseEnter={() => setHoveredBar(bar)}
                    onMouseLeave={() => setHoveredBar(undefined)}
                />
            ) : (
                <motion.rect
                    initial={{ height, width }}
                    animate={{ height, width }}
                    transition={spring}
                    fill={color}
                    opacity={opacity}
                    onMouseEnter={() => setHoveredBar(bar)}
                    onMouseLeave={() => setHoveredBar(undefined)}
                />
            )}

            {showLabels && !hideLabel && (
                <text
                    x={labelX}
                    y={labelY}
                    fill={labelFill}
                    textAnchor='middle'
                    dominantBaseline='central'
                    pointerEvents='none'
                    fontSize={11}
                >
                    {label}
                </text>
            )}
        </>
    );

    if (allowLink && data[`${bar.key}-link`]) {
        return <NavLink to={data[`${bar.key}-link`] as string}>{barComponent}</NavLink>;
    }

    return barComponent;
};
